<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TaskSearchResultsDropdown from 'dashboard/components-next/Tasks/TaskSearchResultsDropdown.vue';

const props = defineProps({
  headerTitle: { type: String, required: true },
  searchValue: { type: String, default: '' },
  viewMode: { type: String, default: 'week' },
  searchResults: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'search',
  'create',
  'update:viewMode',
  'selectSearchResult',
]);

const { t } = useI18n();

const isDropdownDismissed = ref(false);

watch(
  () => props.searchResults,
  () => {
    isDropdownDismissed.value = false;
  }
);

const showDropdown = computed(
  () => props.searchResults.length > 0 && !isDropdownDismissed.value
);

const selectSearchResult = task => {
  isDropdownDismissed.value = true;
  emit('selectSearchResult', task);
};

const VIEW_MODES = ['list', 'day', 'week', 'month'];
</script>

<template>
  <header class="sticky top-0 z-20 px-6 bg-n-surface-1">
    <div
      class="flex items-start sm:items-center justify-between w-full py-4 gap-2 mx-auto"
    >
      <span class="text-xl font-medium truncate text-n-slate-12 flex-shrink-0">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-3">
        <div class="relative flex items-center gap-2 w-full sm:min-w-64">
          <OnClickOutside @trigger="isDropdownDismissed = true">
            <Input
              :model-value="searchValue"
              type="search"
              :placeholder="$t('TASKS.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-full"
              @input="emit('search', $event.target.value)"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
            <TaskSearchResultsDropdown
              v-if="showDropdown"
              :results="searchResults"
              @select="selectSearchResult"
            />
          </OnClickOutside>
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <div
            class="flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak"
          >
            <button
              v-for="mode in VIEW_MODES"
              :key="mode"
              type="button"
              class="px-2.5 h-7 rounded-md text-xs font-medium transition-colors"
              :class="
                viewMode === mode
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="emit('update:viewMode', mode)"
            >
              {{ t(`TASKS.VIEW.${mode.toUpperCase()}`) }}
            </button>
          </div>
          <Button
            :label="t('TASKS.ACTIONS.CREATE')"
            icon="i-lucide-plus"
            size="sm"
            @click="emit('create')"
          />
        </div>
      </div>
    </div>
  </header>
</template>
